/* eslint-disable no-console */
import { makeStyles } from '@mui/styles';
import Loader from 'apollo-react/components/Loader';
import { models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';

import PowerBIPageLayout from './PowerBIPageLayout';
import { handleErrors } from '../../../actions/ErrorActions';
import { clearPowerBIStore, fetchReportConfig } from '../../../actions/PowerBIActions';
import { addSuccess } from '../../../actions/SuccessActions';
import { attachNewReport, unpublishPowerBIRecord } from '../../../api/powerbi';
import {
  MIN_HEADER_HEIGHT,
  POWERBI_ENABLED_APP_SETTING,
} from '../../../constants/PowerBIConstants';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getAppSettingsByKey } from '../../../selectors/AppConfigsSelectors';
import { isTrue } from '../../../utils/commonUtils';
import { getSupportedPowerBILocale } from '../../../utils/powerBIUtils';

const useStyles = makeStyles({
  report: {
    height: '100%',
    width: '100%',
  },
  loaderContainer: {
    width: 'calc(100vw - 135px)',
    position: 'absolute',
    height: ({ headerHeight }) => `calc(100vh - ${headerHeight}px)`,
  },
});

const PowerBIPage = ({ location }) => {
  const isPowerBIEnabled = useSelector(getAppSettingsByKey(POWERBI_ENABLED_APP_SETTING));

  const { itmId, reportName, useUnpublished, datasetId, reportId } = qs.parse(location.search);

  const [isLoading, setLoading] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(MIN_HEADER_HEIGHT);
  const [isEmbedDataReceived, setIsEmbedDataReceived] = useState(false);
  const [reportConfig, setReportConfig] = useState({
    type: datasetId ? 'create' : 'report',
    embedUrl: undefined,
    tokenType: models.TokenType.Embed,
    accessToken: undefined,
    settings: {
      localeSettings: {
        language: getSupportedPowerBILocale(),
      },
    },
  });

  const history = useHistory();
  const classes = useStyles({ headerHeight });
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const pageTitle = reportName || translateString('PowerBI Report');

  useEffect(() => {
    if (isTrue(isPowerBIEnabled)) {
      const getReportConfig = async () => {
        setLoading(true);

        const newReportConfig = await dispatch(
          fetchReportConfig({
            initialConfig: reportConfig,
            datasetId,
            reportId,
            itmId,
            useUnpublished,
          })
        );

        if (!newReportConfig.hasError) {
          setReportConfig(newReportConfig);
          setIsEmbedDataReceived(true);
        }

        setLoading(false);
      };

      getReportConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPowerBIEnabled]);

  useEffect(() => {
    document.title = `${reportName} Report`;

    return () => {
      sessionStorage.removeItem('backButtonURL');
      dispatch(clearPowerBIStore());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eventHandlers = new Map([
    [
      'error',
      (event) => {
        const errorMessage = event.detailedMessage
          ? event.detailedMessage
          : 'Some error has occurred';
        dispatch(handleErrors(errorMessage));
      },
    ],
    [
      'saved',
      async (event) => {
        if (event.detail?.error) {
          dispatch(handleErrors('Some error has occurred'));
          return;
        }

        if (event.detail?.saveAs) {
          const newReportId = event.detail?.reportObjectId;
          const reportName = event.detail?.reportName;

          setLoading(true);
          const response = await attachNewReport(itmId, newReportId, reportName);
          setLoading(false);

          if (response.hasError) {
            handleErrors(response.errors);
            return;
          }

          dispatch(addSuccess({ message: 'Report has been created successfully' }));
        } else {
          setLoading(true);
          const response = await unpublishPowerBIRecord(itmId);
          setLoading(false);

          if (response.hasError) {
            handleErrors(response.errors);
            return;
          }

          dispatch(addSuccess({ message: 'Report has been saved successfully' }));
        }

        history.goBack();
      },
    ],
  ]);

  return (
    <PowerBIPageLayout
      pageTitle={pageTitle}
      headerHeight={headerHeight}
      setHeaderHeight={setHeaderHeight}
      isEmbedDataReceived={isEmbedDataReceived}
    >
      {isTrue(isPowerBIEnabled) && isEmbedDataReceived && (
        <PowerBIEmbed
          embedConfig={reportConfig}
          cssClassName={classes.report}
          eventHandlers={eventHandlers}
        />
      )}
      {isLoading && (
        <div className={classes.loaderContainer}>
          <Loader isInner />
        </div>
      )}
    </PowerBIPageLayout>
  );
};

export default PowerBIPage;
